import type { Box, VAlign } from "./schema.ts";

/** The subset of an opentype.js `Font` the layout engine needs. */
export interface FontLike {
  unitsPerEm: number;
  getAdvanceWidth(text: string, fontSize: number): number;
  getPath(
    text: string,
    x: number,
    y: number,
    fontSize: number,
  ): { toPathData(decimalPlaces?: number): string };
  tables: { os2?: { sCapHeight?: number } };
}

export interface LaidOutLine {
  text: string;
  width: number;
  /** Baseline position in output pixels. */
  x: number;
  y: number;
}

export interface TextLayout {
  fontSize: number;
  lines: LaidOutLine[];
}

const LINE_GAP = 0.28; // gap between lines, as a fraction of cap height
const INNER_PAD = 0.03; // horizontal breathing room inside the box

export function capHeightRatio(font: FontLike): number {
  const cap = font.tables.os2?.sCapHeight;
  return cap && cap > 0 ? cap / font.unitsPerEm : 0.7;
}

function wrap(font: FontLike, words: string[], size: number, maxWidth: number): string[] | null {
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (font.getAdvanceWidth(word, size) > maxWidth) return null;
    const candidate = current ? `${current} ${word}` : word;
    if (font.getAdvanceWidth(candidate, size) <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/**
 * Fit `text` into a pixel box: the largest font size at which the greedy
 * word-wrapped lines fit both dimensions. Deterministic, so the browser
 * preview and the server print render produce identical geometry.
 */
export function layoutText(
  font: FontLike,
  text: string,
  box: { x: number; y: number; w: number; h: number },
  valign: VAlign,
  maxFontSize = Number.POSITIVE_INFINITY,
): TextLayout {
  const words = text.split(" ").filter(Boolean);
  if (words.length === 0) return { fontSize: 0, lines: [] };

  const cap = capHeightRatio(font);
  const maxWidth = box.w * (1 - INNER_PAD * 2);
  const blockHeight = (n: number, size: number) => size * cap * (n + (n - 1) * LINE_GAP);

  let lo = 1;
  let hi = Math.min(maxFontSize, box.h / cap);
  let best: { size: number; lines: string[] } | null = null;
  for (let i = 0; i < 24 && hi - lo > 0.25; i++) {
    const mid = (lo + hi) / 2;
    const lines = wrap(font, words, mid, maxWidth);
    if (lines && blockHeight(lines.length, mid) <= box.h) {
      best = { size: mid, lines };
      lo = mid;
    } else {
      hi = mid;
    }
  }
  if (!best) {
    const lines = wrap(font, words, lo, maxWidth) ?? [words.join(" ")];
    best = { size: lo, lines };
  }

  const size = best.size;
  const capPx = size * cap;
  const total = blockHeight(best.lines.length, size);
  const top =
    valign === "top"
      ? box.y
      : valign === "bottom"
        ? box.y + box.h - total
        : box.y + (box.h - total) / 2;

  return {
    fontSize: size,
    lines: best.lines.map((line, i) => {
      const width = font.getAdvanceWidth(line, size);
      return {
        text: line,
        width,
        x: box.x + (box.w - width) / 2,
        y: top + capPx * (i + 1) + capPx * LINE_GAP * i,
      };
    }),
  };
}

export function boxToPixels(box: Box, width: number, height: number) {
  return { x: box.x * width, y: box.y * height, w: box.w * width, h: box.h * height };
}
